import { Box, Button, Card, Divider, Skeleton, Stack, Typography } from '@mui/material'
import { IntegrationGroup, ZapierTemplate } from '@sendhub/integrations/api-client-typescript-axios'
import { useSelector } from 'react-redux'
import { Route, Link, Routes, useNavigate } from 'react-router-dom'
import { selectAllIntegrationGroupIds, selectAllIntegrationGroups } from '../../../store/integration-group/integration-groups.slice'
import { IntegrationGroupCard, IntegrationGroupCardLinked } from '@sendhub/integrations-ui'
import axios from 'axios'
import { useEffect, useState } from 'react'
import { selectAllZapierApps, selectZapierAppsLoadingStatus } from '../../../store/zapier/zapier-apps.slice'
import { getMainAppSlugFromTemplate, selectAllZapierTemplates } from '../../../store/zapier/zapier-templates.slice'
import { selectIntegrationConfigsLoadingStatus } from '../../../store/integration-config/integration-configs.slice'
import { RequestNewIntegrationFormDialog } from '../../request-new-integration-dialog'
import { INTEGRATIONS_BASE_URL } from '../../../../utils/config'
import { useIntegrationsAccess } from '../../../store/integrations-access'

export interface ZapierCardProps {
  title: string
  imageUrl: string
}

export function ZapierCard({ title, imageUrl }: ZapierCardProps) {
  return <Typography>{title}</Typography>
}

/* eslint-disable-next-line */
export interface ListOfIntegrationGroupsProps {}

export function ListOfIntegrationGroups(props: ListOfIntegrationGroupsProps) {
  const integrationGroups = useSelector(selectAllIntegrationGroups)
  const integrationGroupIds = useSelector(selectAllIntegrationGroupIds)
  const integrationGroupsLoading = useSelector(selectIntegrationConfigsLoadingStatus)

  const zapierApps = useSelector(selectAllZapierApps)
  const zapierAppsLoading = useSelector(selectZapierAppsLoadingStatus)
  const zapierTemplates = useSelector(selectAllZapierTemplates)

  const [formDialogOpen, setFormDialogOpen] = useState(false)
  const navigate = useNavigate()
  const { canEdit } = useIntegrationsAccess()

  const zapierTemplateAppSlugs = new Set(zapierTemplates.map((zapierTemplate) => getMainAppSlugFromTemplate(zapierTemplate)))

  const zapierAppsWithTemplates = zapierApps.filter(
    (zApp) => zapierTemplateAppSlugs.has(zApp.slug) && !integrationGroupIds.includes(zApp.slug.replaceAll(/-/g, '_'))
  )

  return (
    <>
      <RequestNewIntegrationFormDialog close={() => setFormDialogOpen(false)} open={formDialogOpen} />
      <h1 style={{ textAlign: 'center' }}>Available Integrations</h1>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignContent: 'center',
        }}
      >
        {integrationGroups
          .filter((g) => g.id !== 'zapier')
          .map((integrationGroup) => (
            <IntegrationGroupCardLinked
              key={integrationGroup.id}
              link={`${integrationGroup.id}`}
              imageUrl={`${INTEGRATIONS_BASE_URL}${integrationGroup.image}`}
              title={integrationGroup.name}
            />
          ))}
        {zapierAppsWithTemplates.map((zApp) => (
          <IntegrationGroupCardLinked
            key={zApp.title}
            title={zApp.title}
            imageUrl={zApp.image}
            link={`${zApp.slug.replaceAll(/-/g, '_')}/zapier`}
            viaZapier
          />
        ))}
        {integrationGroupsLoading === 'loading' && (
          <>
            {[...Array(3).keys()].map((i) => (
              <Skeleton key={`skeleton-${i}`} variant="rectangular" width={400} height={96} sx={{ m: 2 }} />
            ))}
          </>
        )}
        {zapierAppsLoading === 'loading' && (
          <>
            {[...Array(16).keys()].map((i) => (
              <Skeleton key={`skeleton-${i}`} variant="rectangular" width={400} height={96} sx={{ m: 2 }} />
            ))}
          </>
        )}
        <Box sx={{ width: '100%' }}>
          <Divider sx={{ marginY: 2 }} />
          <Stack direction="column" sx={{ alignItems: 'center', width: '100%', marginY: 2 }}>
            <Typography variant="body2">Not finding what you're looking for?</Typography>
            <Stack direction="row">
              <Button onClick={() => setFormDialogOpen(true)}>Request a Custom Integration</Button>
              {canEdit && (
                <>
                  <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                    or
                  </Typography>
                  <Button onClick={() => navigate('/integrations/sendhub/zapier')}>Connect SendHub to 5,000+ Apps via Zapier</Button>
                </>
              )}
            </Stack>
          </Stack>
        </Box>
      </Box>
    </>
  )
}

export default ListOfIntegrationGroups
